import { useState, useEffect, useRef, useCallback } from 'react';

// Custom hook para WebSocket de tiempo real empresarial
export const useWebSocket = (user, backendUrl) => {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [notifications, setNotifications] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showTaskAlarm, setShowTaskAlarm] = useState(false);
  const [alarmData, setAlarmData] = useState(null);
  
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  
  // Función para conectar WebSocket
  const connect = useCallback(() => {
    if (!user?.access_token || !backendUrl) {
      return;
    }
    
    try {
      setConnectionStatus('connecting');
      
      // URL del WebSocket usando el token JWT
      const wsUrl = backendUrl.replace('http', 'ws') + `/ws/${user.access_token}`;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setConnectionStatus('connected');
        setSocket(ws);
        
        // Heartbeat para mantener conexión viva (cada 45 segundos)
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 45000);
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          // Silenciar errores de parsing
        }
      };
      
      ws.onclose = (event) => {
        // Solo log si no es un cierre normal
        if (event.code !== 1000 && event.code !== 1006) {
          console.log('WebSocket cerrado:', event.code);
        }
        setConnectionStatus('disconnected');
        setSocket(null);
        
        // Limpiar heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        // Reconectar automáticamente con backoff (excepto si fue cierre intencional)
        if (event.code !== 1000 && user?.access_token) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 15000); // Reconectar cada 15 segundos (antes era 5)
        }
      };
      
      ws.onerror = (error) => {
        // Silenciar errores de conexión para no llenar la consola
        setConnectionStatus('error');
      };
      
    } catch (error) {
      console.error('Error conectando WebSocket:', error);
      setConnectionStatus('error');
    }
  }, [user?.access_token, backendUrl]);
  
  // Manejar diferentes tipos de mensajes WebSocket
  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'connected':
        // Conexión exitosa, no hacer nada
        break;
        
      case 'TASK_ALARM':
        // 🚨 ALARMA DE NUEVA TAREA - PANTALLA COMPLETA
        setAlarmData(message.data);
        setShowTaskAlarm(true);
        
        // Reproducir sonido de alarma (si está disponible)
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fDbyjq4EcZAC6A==');
          audio.play().catch(() => {}); // Ignore si no puede reproducir
        } catch (e) {}
        
        addNotification({
          id: Date.now(),
          type: 'alarm',
          title: '🚨 NUEVA TAREA PROGRAMADA',
          message: `${message.data.nombre} - ${message.data.bodega}`,
          data: message.data,
          priority: 'critical'
        });
        break;
        
      case 'accounting_created':
        addNotification({
          id: Date.now(),
          type: 'accounting',
          title: 'Nuevo Registro Contable',
          message: message.message,
          data: message.data,
          priority: 'normal'
        });
        break;
        
      case 'order_created':
        addNotification({
          id: Date.now(),
          type: 'order',
          title: 'Nueva Orden Creada',
          message: message.message,
          data: message.data,
          priority: 'normal'
        });
        break;
        
      case 'user_connected':
        console.log(`👤 ${message.data.username} se conectó`);
        setOnlineUsers(prev => prev.filter(u => u !== message.data.username).concat([message.data.username]));
        break;
        
      case 'user_disconnected':
        console.log(`👤 ${message.data.username} se desconectó`);
        setOnlineUsers(prev => prev.filter(u => u !== message.data.username));
        break;
        
      case 'admin_broadcast':
        addNotification({
          id: Date.now(),
          type: 'admin',
          title: `📢 Mensaje de ${message.from}`,
          message: message.data.message || 'Mensaje administrativo',
          data: message.data,
          priority: 'high'
        });
        break;
        
      case 'pong':
        // Respuesta al ping - conexión activa
        break;
        
      default:
        console.log('Mensaje WebSocket no manejado:', message);
    }
  };
  
  // Agregar notificación
  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 9)]); // Máximo 10 notificaciones
    
    // Auto-remover notificaciones normales después de 5 segundos
    if (notification.priority !== 'critical') {
      setTimeout(() => {
        removeNotification(notification.id);
      }, 5000);
    }
  };
  
  // Remover notificación
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  
  // Enviar mensaje via WebSocket
  const sendMessage = useCallback((message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  }, [socket]);
  
  // Cerrar alarma de tarea
  const closeTaskAlarm = () => {
    setShowTaskAlarm(false);
    setAlarmData(null);
  };
  
  // Conectar cuando el componente se monta
  useEffect(() => {
    console.log('🔍 useWebSocket - Verificando usuario:', user);
    console.log('🔍 useWebSocket - Tiene access_token:', !!user?.access_token);
    console.log('🔍 useWebSocket - backendUrl:', backendUrl);
    
    if (user?.access_token) {
      console.log('🚀 Iniciando conexión WebSocket...');
      connect();
    } else {
      console.log('⚠️ No se puede conectar WebSocket - Falta access_token');
    }
    
    return () => {
      // Limpiar al desmontar
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (socket) {
        socket.close(1000, 'Component unmounting');
      }
    };
  }, [user?.access_token, connect]);
  
  return {
    // Estado de conexión
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    
    // Notificaciones
    notifications,
    removeNotification,
    clearNotifications: () => setNotifications([]),
    
    // Usuarios online
    onlineUsers,
    
    // Alarma de tareas
    showTaskAlarm,
    alarmData,
    closeTaskAlarm,
    
    // Funciones
    sendMessage,
    reconnect: connect,
    
    // Socket crudo (para casos especiales)
    socket
  };
};